import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Search, HelpCircle, Tag, Package } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const QuestionBankPage = () => {
  const [questions, setQuestions] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterGroup, setFilterGroup] = useState('all');
  const [groups, setGroups] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [form, setForm] = useState({
    text: '', type: 'texto', category: 'generica', product_ids: [],
    options: [], required: false, order: 0, variable_name: '', group: '', help_text: ''
  });
  const [optionInput, setOptionInput] = useState('');

  const loadData = useCallback(async () => {
    try {
      const [qRes, pRes] = await Promise.all([
        axios.get(`${API_URL}/diagnostic-questions`, { withCredentials: true }),
        axios.get(`${API_URL}/products`, { withCredentials: true })
      ]);
      setQuestions(qRes.data);
      setProducts(pRes.data);
      const allGroups = [...new Set(qRes.data.map(q => q.group).filter(Boolean))];
      setGroups(allGroups);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const resetForm = () => {
    setForm({ text: '', type: 'texto', category: 'generica', product_ids: [], options: [], required: false, order: 0, variable_name: '', group: '', help_text: '' });
    setOptionInput('');
    setEditingQuestion(null);
  };

  const openNew = () => { resetForm(); setDialogOpen(true); };

  const openEdit = (q) => {
    setEditingQuestion(q);
    setForm({
      text: q.text, type: q.type, category: q.category, product_ids: q.product_ids || [],
      options: q.options || [], required: q.required, order: q.order, variable_name: q.variable_name || '',
      group: q.group || '', help_text: q.help_text || ''
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.text.trim()) { toast.error('Texto da pergunta é obrigatório'); return; }
    try {
      if (editingQuestion) {
        await axios.put(`${API_URL}/diagnostic-questions/${editingQuestion.question_id}`, form, { withCredentials: true });
        toast.success('Pergunta atualizada');
      } else {
        await axios.post(`${API_URL}/diagnostic-questions`, form, { withCredentials: true });
        toast.success('Pergunta criada');
      }
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar');
    }
  };

  const handleDelete = async (q) => {
    if (!window.confirm(`Desativar pergunta "${q.text}"?`)) return;
    try {
      await axios.delete(`${API_URL}/diagnostic-questions/${q.question_id}`, { withCredentials: true });
      toast.success('Pergunta desativada');
      loadData();
    } catch (error) {
      toast.error('Erro ao desativar');
    }
  };

  const addOption = () => {
    if (optionInput.trim()) {
      setForm(prev => ({ ...prev, options: [...prev.options, optionInput.trim()] }));
      setOptionInput('');
    }
  };

  const removeOption = (idx) => {
    setForm(prev => ({ ...prev, options: prev.options.filter((_, i) => i !== idx) }));
  };

  const toggleProduct = (productId) => {
    setForm(prev => ({
      ...prev,
      product_ids: prev.product_ids.includes(productId)
        ? prev.product_ids.filter(id => id !== productId)
        : [...prev.product_ids, productId]
    }));
  };

  const filtered = questions.filter(q => {
    if (search && !q.text.toLowerCase().includes(search.toLowerCase()) && !q.variable_name?.toLowerCase().includes(search.toLowerCase())) return false;
    if (filterCategory !== 'all' && q.category !== filterCategory) return false;
    if (filterGroup !== 'all' && q.group !== filterGroup) return false;
    return true;
  });

  const typeLabels = { texto: 'Texto', numero: 'Número', selecao: 'Seleção', sim_nao: 'Sim/Não', data: 'Data' };
  const categoryLabels = { generica: 'Genérica', produto: 'Por Produto' };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-10 h-10 border-4 border-[#AF2628] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="question-bank-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#111111]">Banco de Perguntas</h1>
          <p className="text-sm text-[#6E6E73] mt-1">Gerencie as perguntas do diagnóstico de oportunidades</p>
        </div>
        <Button onClick={openNew} className="bg-[#AF2628] hover:bg-[#961B22]" data-testid="new-question-btn">
          <Plus className="w-4 h-4 mr-2" /> Nova Pergunta
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="flex-1 min-w-[200px]">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#6E6E73]" />
            <Input placeholder="Buscar pergunta ou variável..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10" data-testid="question-search" />
          </div>
        </div>
        <Select value={filterCategory} onValueChange={setFilterCategory}>
          <SelectTrigger className="w-[180px]" data-testid="filter-category">
            <SelectValue placeholder="Categoria" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas Categorias</SelectItem>
            <SelectItem value="generica">Genéricas</SelectItem>
            <SelectItem value="produto">Por Produto</SelectItem>
          </SelectContent>
        </Select>
        {groups.length > 0 && (
          <Select value={filterGroup} onValueChange={setFilterGroup}>
            <SelectTrigger className="w-[180px]" data-testid="filter-group">
              <SelectValue placeholder="Grupo" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos Grupos</SelectItem>
              {groups.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Questions List */}
      <div className="bg-white border border-[#E5E5EA] rounded-xl overflow-hidden">
        {filtered.length > 0 ? (
          <div className="divide-y divide-[#E5E5EA]">
            {filtered.map((q) => (
              <div key={q.question_id} className="p-4 hover:bg-[#FAFAFA] transition-colors" data-testid={`question-${q.question_id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <HelpCircle className="w-4 h-4 text-[#AF2628] shrink-0" />
                      <h3 className="font-medium text-sm text-[#111111]">{q.text}</h3>
                      {q.required && <span className="text-[10px] bg-red-50 text-red-600 px-1.5 py-0.5 rounded-full font-medium">Obrigatória</span>}
                    </div>
                    <div className="flex flex-wrap items-center gap-2 mt-2 text-xs text-[#6E6E73]">
                      <span className="bg-gray-100 px-2 py-0.5 rounded-full">{typeLabels[q.type] || q.type}</span>
                      <span className={`px-2 py-0.5 rounded-full ${q.category === 'generica' ? 'bg-blue-50 text-blue-600' : 'bg-amber-50 text-amber-600'}`}>
                        {categoryLabels[q.category]}
                      </span>
                      {q.group && <span className="bg-purple-50 text-purple-600 px-2 py-0.5 rounded-full">{q.group}</span>}
                      {q.variable_name && (
                        <span className="bg-emerald-50 text-emerald-600 px-2 py-0.5 rounded-full font-mono text-[10px]">
                          {'{{' + q.variable_name + '}}'}
                        </span>
                      )}
                    </div>
                    {q.product_names?.length > 0 && (
                      <div className="flex flex-wrap items-center gap-1.5 mt-2">
                        <Package className="w-3.5 h-3.5 text-[#6E6E73]" />
                        {q.product_names.map((name, i) => (
                          <span key={`${q.question_id}-${name}-${i}`} className="text-[11px] bg-gray-100 text-[#6E6E73] px-2 py-0.5 rounded-full">{name}</span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(q)} data-testid={`edit-question-${q.question_id}`}>
                      <Pencil className="w-4 h-4 text-[#6E6E73]" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(q)} data-testid={`delete-question-${q.question_id}`}>
                      <Trash2 className="w-4 h-4 text-red-400" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-10 text-center">
            <HelpCircle className="w-10 h-10 mx-auto mb-3 text-[#E5E5EA]" />
            <p className="text-sm text-[#6E6E73]">Nenhuma pergunta cadastrada</p>
            <Button variant="link" onClick={openNew} className="text-[#AF2628] mt-2">Criar primeira pergunta</Button>
          </div>
        )}
      </div>

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingQuestion ? 'Editar Pergunta' : 'Nova Pergunta'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label>Texto da Pergunta *</Label>
              <Textarea value={form.text} onChange={e => setForm(prev => ({ ...prev, text: e.target.value }))} placeholder="Ex: Qual o prazo de vigência do contrato?" data-testid="question-text-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Tipo de Resposta</Label>
                <Select value={form.type} onValueChange={v => setForm(prev => ({ ...prev, type: v }))}>
                  <SelectTrigger data-testid="question-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="texto">Texto</SelectItem>
                    <SelectItem value="numero">Número</SelectItem>
                    <SelectItem value="selecao">Seleção</SelectItem>
                    <SelectItem value="sim_nao">Sim/Não</SelectItem>
                    <SelectItem value="data">Data</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Categoria</Label>
                <Select value={form.category} onValueChange={v => setForm(prev => ({ ...prev, category: v }))}>
                  <SelectTrigger data-testid="question-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="generica">Genérica (sempre visível)</SelectItem>
                    <SelectItem value="produto">Por Produto</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Grupo (agrupamento visual)</Label>
                <Input value={form.group} onChange={e => setForm(prev => ({ ...prev, group: e.target.value }))} placeholder="Ex: Comercial, Técnico, Jurídico" data-testid="question-group-input" />
              </div>
              <div>
                <Label>Nome da Variável</Label>
                <Input value={form.variable_name} onChange={e => setForm(prev => ({ ...prev, variable_name: e.target.value }))} placeholder="Ex: prazo_vigencia" className="font-mono text-sm" data-testid="question-variable-input" />
                {form.variable_name && <p className="text-[11px] text-emerald-600 mt-1">No template: {'{{' + form.variable_name + '}}'}</p>}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Ordem de Exibição</Label>
                <Input type="number" value={form.order} onChange={e => setForm(prev => ({ ...prev, order: parseInt(e.target.value) || 0 }))} />
              </div>
              <div className="flex items-center gap-2 pt-6">
                <input type="checkbox" id="required" checked={form.required} onChange={e => setForm(prev => ({ ...prev, required: e.target.checked }))} className="rounded" />
                <Label htmlFor="required" className="cursor-pointer">Resposta obrigatória</Label>
              </div>
            </div>
            <div>
              <Label>Texto de Ajuda (opcional)</Label>
              <Input value={form.help_text} onChange={e => setForm(prev => ({ ...prev, help_text: e.target.value }))} placeholder="Instrução para o preenchimento" />
            </div>

            {form.type === 'selecao' && (
              <div>
                <Label>Opções de Resposta</Label>
                <div className="flex gap-2 mt-1">
                  <Input value={optionInput} onChange={e => setOptionInput(e.target.value)} placeholder="Adicionar opção" onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addOption())} />
                  <Button type="button" variant="outline" onClick={addOption}>Adicionar</Button>
                </div>
                {form.options.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {form.options.map((opt, i) => (
                      <span key={`${opt}-${i}`} className="bg-gray-100 px-3 py-1 rounded-full text-sm flex items-center gap-1.5">
                        {opt}
                        <button onClick={() => removeOption(i)} className="text-red-400 hover:text-red-600"><X className="w-3 h-3" /></button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}

            {form.category === 'produto' && (
              <div>
                <Label>Produtos Vinculados</Label>
                <p className="text-xs text-[#6E6E73] mb-2">Selecione os produtos para os quais esta pergunta aparecerá</p>
                <div className="border border-[#E5E5EA] rounded-lg max-h-48 overflow-y-auto p-2 space-y-1">
                  {products.map(p => (
                    <label key={p.product_id} className="flex items-center gap-2 p-2 rounded hover:bg-gray-50 cursor-pointer">
                      <input type="checkbox" checked={form.product_ids.includes(p.product_id)} onChange={() => toggleProduct(p.product_id)} className="rounded" />
                      <span className="text-sm">{p.name}</span>
                      {p.codigo && <span className="text-[10px] text-[#AEAEB2]">{p.codigo}</span>}
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
              <Button onClick={handleSave} className="bg-[#AF2628] hover:bg-[#961B22]" data-testid="save-question-btn">
                {editingQuestion ? 'Atualizar' : 'Criar Pergunta'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const X = ({ className }) => <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12"/></svg>;

export default QuestionBankPage;
